import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { analytics } from '../services/analytics';
import { BarChart3, TrendingUp, Users, Eye, Target, Clock, ArrowUpRight, Lock, Crown } from 'lucide-react';

const Analytics = () => {
  const { user } = useAuth();
  const [analyticsData, setAnalyticsData] = useState(null);
  const [quoteAnalytics, setQuoteAnalytics] = useState(null);
  const [conversionData, setConversionData] = useState(null);
  const [timeframe, setTimeframe] = useState('7d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscriptionTier, setSubscriptionTier] = useState('freemium');

  // Check user's subscription status
  useEffect(() => {
    const checkSubscription = async () => {
      try {
        // Get user profile to check subscription
        const token = localStorage.getItem('token');
        const response = await fetch('/api/profile', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const profile = await response.json();
          setSubscriptionTier(profile.subscription_tier || 'freemium');
        }
      } catch (error) {
        console.error('Failed to check subscription:', error);
      }
    };

    checkSubscription();
  }, []);

  // Track analytics page view
  useEffect(() => {
    analytics.trackPageView('Analytics');
  }, []);

  // Fetch analytics data
  const fetchAnalyticsData = async () => {
    if (subscriptionTier === 'freemium') {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      // Fetch all analytics data in parallel
      const [dashboardResponse, quotesResponse, conversionResponse] = await Promise.all([
        fetch(`/api/analytics/dashboard?timeframe=${timeframe}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`/api/analytics/quotes?timeframe=${timeframe}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`/api/analytics/conversion?timeframe=${timeframe}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (!dashboardResponse.ok) {
        const errorData = await dashboardResponse.json();
        throw new Error(errorData.error || 'Failed to fetch analytics');
      }

      const [dashboard, quotes, conversion] = await Promise.all([
        dashboardResponse.json(),
        quotesResponse.ok ? quotesResponse.json() : {},
        conversionResponse.ok ? conversionResponse.json() : {}
      ]);

      setAnalyticsData(dashboard);
      setQuoteAnalytics(quotes);
      setConversionData(conversion);

    } catch (error) {
      console.error('Analytics fetch error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalyticsData();
  }, [timeframe, subscriptionTier]);

  // Freemium restriction component
  const FreemiumRestriction = () => (
    <div className="min-h-screen bg-white text-black flex items-center justify-center">
      <div className="max-w-2xl mx-auto px-6 text-center">
        <div className="border border-black rounded-2xl p-12">
          <div className="flex items-center justify-center mb-6">
            <div className="p-4 border border-black rounded-full">
              <Lock className="w-12 h-12 text-black" />
            </div>
          </div>

          <div className="inline-flex items-center px-3 py-1 border border-black rounded-full mb-4">
            <Crown className="w-4 h-4 mr-2" />
            <span className="font-jetbrains text-sm">Pro</span>
          </div>

          <h1 className="font-instrument text-4xl tracking-tight mb-3">Unlock Pro Analytics</h1>
          <p className="font-jetbrains text-lg text-gray-600 mb-8">
            Deeper insights to grow revenue with QuoteTier Pro.
          </p>

          <div className="bg-gray-50 rounded-xl p-6 mb-8 border border-black/10 text-left">
            <h3 className="font-instrument text-2xl mb-4 flex items-center">
              <Crown className="w-6 h-6 mr-2" />
              What you get
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-3">
                <BarChart3 className="w-5 h-5 text-black" />
                <span className="font-jetbrains">Detailed usage metrics</span>
              </div>
              <div className="flex items-center space-x-3">
                <TrendingUp className="w-5 h-5 text-black" />
                <span className="font-jetbrains">Quote conversion tracking</span>
              </div>
              <div className="flex items-center space-x-3">
                <Users className="w-5 h-5 text-black" />
                <span className="font-jetbrains">Client engagement insights</span>
              </div>
              <div className="flex items-center space-x-3">
                <Target className="w-5 h-5 text-black" />
                <span className="font-jetbrains">Performance optimization</span>
              </div>
              <div className="flex items-center space-x-3">
                <Eye className="w-5 h-5 text-black" />
                <span className="font-jetbrains">Real-time activity tracking</span>
              </div>
              <div className="flex items-center space-x-3">
                <Clock className="w-5 h-5 text-black" />
                <span className="font-jetbrains">Historical trend analysis</span>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <Link
              to="/profile"
              className="inline-flex items-center px-8 py-4 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-jetbrains font-medium"
            >
              <Crown className="w-5 h-5 mr-2" />
              Upgrade to Pro
              <ArrowUpRight className="w-5 h-5 ml-2" />
            </Link>
            <div className="text-sm text-gray-600 font-jetbrains">
              Or <Link to="/dashboard" className="text-black underline">return to dashboard</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Show freemium restriction if user doesn't have pro access
  if (subscriptionTier === 'freemium') {
    return <FreemiumRestriction />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-black flex items-center justify-center">
        <div className="text-center">
          <div className="relative w-12 h-12 mx-auto mb-6">
            <div className="w-12 h-12 rounded-full border-2 border-gray-200"></div>
            <div className="spinner spinner-lg absolute top-0 left-0"></div>
          </div>
          <p className="font-jetbrains text-gray-600 text-lg">
            Loading analytics...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white text-black flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="font-instrument text-2xl mb-4">Analytics Error</h2>
            <p className="font-jetbrains text-red-700 mb-4">{error}</p>
            <button 
              onClick={fetchAnalyticsData}
              className="px-6 py-2 bg-black text-white rounded hover:bg-gray-800 font-jetbrains"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-black">
      {/* Header */}
      <div className="border-b border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 md:py-8">
          <div className="flex items-center justify-between">
            <h1 className="font-instrument text-2xl md:text-4xl tracking-tight">Analytics</h1>
            
            {/* Timeframe Selector */}
            <div className="flex items-center space-x-4">
              <select 
                value={timeframe} 
                onChange={(e) => setTimeframe(e.target.value)}
                className="font-jetbrains px-4 py-2 border border-black rounded bg-white"
              >
                <option value="1d">Last 24 hours</option>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
              </select>
              
              <Link 
                to="/dashboard" 
                className="font-jetbrains text-black hover:text-gray-700"
              >
                ← Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <div className="border border-black rounded-lg p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-jetbrains font-medium">Total Events</h3>
              <BarChart3 className="w-5 h-5 text-gray-600" />
            </div>
            <div className="font-instrument text-3xl">
              {analyticsData?.overview?.total_events || 0}
            </div>
            <p className="font-jetbrains text-sm text-gray-600 mt-2">
              {timeframe} timeframe
            </p>
          </div>

          <div className="border border-black rounded-lg p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-jetbrains font-medium">Unique Sessions</h3>
              <Users className="w-5 h-5 text-gray-600" />
            </div>
            <div className="font-instrument text-3xl">
              {analyticsData?.overview?.unique_sessions || 0}
            </div>
            <p className="font-jetbrains text-sm text-gray-600 mt-2">
              Active user sessions
            </p>
          </div>

          <div className="border border-black rounded-lg p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-jetbrains font-medium">Quotes Created</h3>
              <Target className="w-5 h-5 text-gray-600" />
            </div>
            <div className="font-instrument text-3xl">
              {analyticsData?.conversion_metrics?.quotes_created || 0}
            </div>
            <p className="font-jetbrains text-sm text-gray-600 mt-2">
              New quotes generated
            </p>
          </div>

          <div className="border border-black rounded-lg p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-jetbrains font-medium">Conversion Rate</h3>
              <TrendingUp className="w-5 h-5 text-gray-600" />
            </div>
            <div className="font-instrument text-3xl">
              {analyticsData?.conversion_metrics?.conversion_rate || 0}%
            </div>
            <p className="font-jetbrains text-sm text-gray-600 mt-2">
              Quote acceptance rate
            </p>
          </div>
        </div>

        {/* Event Types Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <div className="border border-black rounded-lg p-6">
            <h3 className="font-instrument text-2xl mb-6">Event Types</h3>
            <div className="space-y-4">
              {analyticsData?.events_by_type && Object.entries(analyticsData.events_by_type).map(([event, count]) => (
                <div key={event} className="flex items-center justify-between">
                  <span className="font-jetbrains">{event}</span>
                  <span className="font-instrument text-xl">{count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="border border-black rounded-lg p-6">
            <h3 className="font-instrument text-2xl mb-6">Top Pages</h3>
            <div className="space-y-4">
              {analyticsData?.top_pages && Object.entries(analyticsData.top_pages).map(([page, visits]) => (
                <div key={page} className="flex items-center justify-between">
                  <span className="font-jetbrains text-sm truncate">{page}</span>
                  <span className="font-instrument text-xl">{visits}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Quote Analytics */}
        {quoteAnalytics && (
          <div className="border border-black rounded-lg p-6 mb-8">
            <h3 className="font-instrument text-2xl mb-6">Quote Performance</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="font-instrument text-4xl text-green-600 mb-2">
                  {quoteAnalytics.total_quotes_created || 0}
                </div>
                <p className="font-jetbrains text-gray-600">Quotes Created</p>
              </div>
              <div className="text-center">
                <div className="font-instrument text-4xl text-blue-600 mb-2">
                  {quoteAnalytics.total_quotes_viewed || 0}
                </div>
                <p className="font-jetbrains text-gray-600">Quotes Viewed</p>
              </div>
              <div className="text-center">
                <div className="font-instrument text-4xl text-purple-600 mb-2">
                  {quoteAnalytics.total_quotes_accepted || 0}
                </div>
                <p className="font-jetbrains text-gray-600">Quotes Accepted</p>
              </div>
            </div>
          </div>
        )}

        {/* Pro Badge */}
        <div className="text-center mt-12">
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-yellow-100 to-orange-100 border border-yellow-300 rounded-full">
            <Crown className="w-4 h-4 text-yellow-600 mr-2" />
            <span className="font-jetbrains text-sm font-medium text-yellow-800">
              Pro Analytics Enabled
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
